import {
   Patient,
   PatientAlreadyExistsFault,
   PatientMergeCommand,
   PatientUpdateCommand,
} from '@dreryk/egabinet-patient-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import {
   ButtonVariant,
   dispatchSnackbarPositiveEvent,
   ModalConfirmButton,
   useHistoryPushWithBackAction,
   useLocationWithBackState,
} from '@dreryk/react-components';

import { patientPaths } from '~libs.desktop.shared.paths';

import { PersonalDataFormBean } from '../../common/common.types';
import { patientPersonalDataCommandTransformer } from '../../common/common.utils';
import { patientTranslationKeys } from '../../translations';
import { PatientUnconfirmedFormController } from '../patientUnconfirmedUpdate.types';
import { usePatientAlreadyExistsFaultHandler } from '../../common/common.hooks';

type PatientAssociateModalConfirmButtonProps = {
   readonly patientId: number;
   readonly unconfirmedPatientId: number;
   readonly handleSubmit: PatientUnconfirmedFormController['handleSubmit'];
   readonly onSuccess: VoidFunction;
};

const usePatientUpdateCommand = (
   patientId: number,
   onSuccess: () => Promise<Patient>
): UseCqrsCommandResult<Patient, PersonalDataFormBean, PatientAlreadyExistsFault> => {
   const push = useHistoryPushWithBackAction(false);
   const location = useLocationWithBackState();
   const patientAlreadyExistsFaultHandler = usePatientAlreadyExistsFaultHandler();

   return useCqrsCommand(
      (parameters) => {
         const command = new PatientUpdateCommand();
         patientPersonalDataCommandTransformer(command, parameters);
         command.id = patientId;
         return command;
      },
      {
         onSuccess: async () => {
            await onSuccess();
            push(patientPaths.getDataPath(patientId), { label: location?.state.label });
         },
         onFault: {
            'egabinet-patient-frontend-api:PatientAlreadyExistsFault': patientAlreadyExistsFaultHandler,
         },
      }
   );
};

const usePatientMergeCommand = (
   patientId: number,
   unconfirmedPatientId: number,
   onSuccess: VoidFunction
): UseCqrsCommandResult<Patient, void, PatientAlreadyExistsFault> => {
   return useCqrsCommand(
      () => {
         const command = new PatientMergeCommand();
         command.mergeTargetPatientId = patientId;
         command.unconfirmedPatientId = unconfirmedPatientId;
         return command;
      },
      {
         onSuccess: () => {
            onSuccess();
            dispatchSnackbarPositiveEvent(patientTranslationKeys.unconfirmed.patientsFilesAssociated);
         },
      }
   );
};

export const PatientAssociateModalConfirmButton = (props: PatientAssociateModalConfirmButtonProps): JSX.Element => {
   const { executeAsync: mergePatient } = usePatientMergeCommand(
      props.patientId,
      props.unconfirmedPatientId,
      props.onSuccess
   );
   const { executeAsync: updatePatient } = usePatientUpdateCommand(props.patientId, mergePatient);

   return (
      <ModalConfirmButton
         testId={'unconfirmedPatientUpdate'}
         label={patientTranslationKeys.unconfirmed.associateFiles}
         variant={ButtonVariant.PRIMARY}
         onClick={props.handleSubmit(updatePatient)}
         blockingOperation
      />
   );
};
